/**
 * Eerst aankondigen, dan pas beginnen.
 *
 * Zie `docs/architecture/gelijktijdigheid.md` §7. Een import of restore begint
 * niet zomaar: wie in het geraakte workpackage werkt krijgt een aftelling.
 * Deze functie doet die aankondiging en komt pas terug wanneer het zover is.
 *
 * Is er niemand anders verbonden, dan is er niets aan te kondigen en komt 'ie
 * meteen terug — met de locking-laag is dat exact te bepalen in plaats van te
 * schatten.
 *
 * **Waarom het wachten hier gebeurt en niet aan de serverkant.** De payload van
 * een import kan megabytes zijn en hoort niet in D1 te liggen wachten. De
 * aanvrager houdt 'm vast; sluit 'ie z'n scherm, dan gebeurt er niets. Dat is
 * geen tekortkoming maar de bedoeling: een geplande operatie zonder aanvrager
 * is geen operatie.
 */
import { api, type OperationPreview } from "../api/client";
import { useOperations } from "./operations";

export type AnnounceOutcome =
  | { go: true; operationId: string | null }
  | { go: false; reason: "cancelled" };

/**
 * De droge run laten zien en op een antwoord wachten. De aanvrager beslist
 * hier, vóór er iemand anders lastiggevallen wordt met een aftelling: blijkt
 * uit de lijst dat 'ie het verkeerde bestand te pakken heeft, dan is er nog
 * niets gebeurd en heeft niemand iets gemerkt.
 *
 * De UI levert de vraagsteller aan; deze module weet niets van schermen.
 */
export type PreviewAsker = (preview: OperationPreview) => Promise<boolean>;

/**
 * De omvang zoals de aankondiging 'm meestuurt: vier getallen, geen lijsten.
 * De balk toont een regel; wie de namen wil ziet ze in het venster erachter.
 */
export function impactFromPreview(preview: OperationPreview): Record<string, number> {
  return {
    nieuw: preview.created.concepts + preview.created.relations + preview.created.diagrams,
    overschreven: preview.overwritten_counts.concepts + preview.overwritten_counts.relations,
    verdwijnt: preview.removed_counts.concepts + preview.removed_counts.relations,
    diagrammen: preview.diagrams.length,
  };
}

/** Hoe vaak we kijken of het zover is. De balk peilt zelf al; dit is de tweede
 *  lezer van dezelfde waarheid, niet een tweede waarheid. */
const CHECK_MS = 3_000;

export async function announceThenWait(
  workspaceId: string,
  branchId: string,
  request: {
    kind: "import" | "restore" | "merge";
    label: string;
    /** Weglaten laat de server de scope afleiden — uit de snapshot bij een
     *  restore, anders uit de branch waarin gewerkt wordt. */
    workpackageIds?: string[];
    snapshotId?: string;
    impact?: Record<string, number>;
    /** De droge run, en wie 'm voorlegt. Beide of geen van beide. */
    preview?: OperationPreview;
    askPreview?: PreviewAsker;
  },
): Promise<AnnounceOutcome> {
  if (request.preview && request.askPreview) {
    const akkoord = await request.askPreview(request.preview);
    if (!akkoord) return { go: false, reason: "cancelled" };
  }

  // Faalt het aankondigen, dan gaat de operatie door. Dezelfde regel als bij de
  // locking-laag zelf: een voorziening die zichzelf niet kan bevestigen mag het
  // werk niet stilleggen. Wat je verliest is de aankondiging, niet de import.
  let announced: Awaited<ReturnType<typeof api.announceOperation>>;
  try {
    announced = await api.announceOperation(workspaceId, branchId, {
      kind: request.kind,
      label: request.label,
      ...(request.workpackageIds ? { workpackage_ids: request.workpackageIds } : {}),
      ...(request.snapshotId ? { snapshot_id: request.snapshotId } : {}),
      ...(request.impact ? { impact: request.impact } : {}),
      ...(request.preview ? { preview: request.preview } : {}),
    });
  } catch (err) {
    console.error("[announceFirst] aankondigen mislukt; toch doorgegaan", err);
    return { go: true, operationId: null };
  }
  if (!announced.announced || !announced.operation) {
    return { go: true, operationId: null };
  }

  const id = announced.operation.id;
  // De balk verschijnt bij iedereen, inclusief de aanvrager zelf: die heeft er
  // z'n eigen knoppen (nu starten, afblazen).
  await useOperations.getState().refresh();

  return new Promise<AnnounceOutcome>((resolve) => {
    const tick = async () => {
      let current: Awaited<ReturnType<typeof api.listActiveOperations>>;
      try {
        current = await api.listActiveOperations(workspaceId, branchId);
      } catch {
        return; // volgende ronde opnieuw; een netwerkfout zegt niets
      }
      const op = current.operations.find((o) => o.id === id);
      if (!op) {
        // Weg uit de lijst betekent afgeblazen of afgerond door iemand anders.
        clearInterval(timer);
        resolve({ go: false, reason: "cancelled" });
        return;
      }
      if (op.phase === "due") {
        clearInterval(timer);
        void api.setOperationState(workspaceId, branchId, id, "running").catch(() => {});
        resolve({ go: true, operationId: id });
      }
    };
    const timer = setInterval(() => void tick(), CHECK_MS);
    void tick();
  });
}

/** Afronden: het slot van de stilte eraf en de balk van ieders scherm. Faalt
 *  stil — een geslaagde import mag niet alsnog omvallen op de boekhouding. */
export async function finishOperation(
  workspaceId: string,
  branchId: string,
  operationId: string | null,
): Promise<void> {
  if (!operationId) return;
  await api.setOperationState(workspaceId, branchId, operationId, "done").catch(() => {});
  await useOperations.getState().refresh();
}
